<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AutomationConditionGroup from 'dashboard/components/widgets/FilterInput/AutomationConditionGroup.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useAutomationConditionGroups } from 'dashboard/composables/useAutomationConditionGroups';
import { validateAutomation } from 'dashboard/helper/validations';
import { WEBCHAT_LINK_UNOPENED_EVENT } from 'dashboard/constants/automation';
import {
  generateAutomationPayload,
  getAttributes,
  getInputType,
  getOperators,
  getCustomAttributeType,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  WEBCHAT_LINK_UNOPENED_ACTIONS,
} from 'dashboard/routes/dashboard/settings/automation/constants';

// AutomationForm — corpo + footer sticky do formulário de Automação.
// Presentacional: recebe o `automation` (ref via prop) já preparado pela
// página (create monta default, edit monta via formatAutomation) e os
// helpers do useAutomation. Gere os condition groups internamente via
// useAutomationConditionGroups (lógica portada 1:1 do legado).
//
// Header fica na página (PageHeader). Aqui: flex column de altura cheia —
// abas em área flex-1 com scroll interno + footer shrink-0 fixo embaixo
// (não se move ao trocar de aba).
const props = defineProps({
  automationTypes: { type: Object, required: true },
  mode: {
    type: String,
    required: true,
    validator: v => ['create', 'edit'].includes(v),
  },
  isSubmitting: { type: Boolean, default: false },
  onEventChange: { type: Function, required: true },
  getConditionDropdownValues: { type: Function, required: true },
  getActionDropdownValues: { type: Function, required: true },
  appendNewAction: { type: Function, required: true },
  removeAction: { type: Function, required: true },
  resetAction: { type: Function, required: true },
  allCustomAttributes: { type: Array, default: () => [] },
});

const emit = defineEmits(['submit', 'cancel']);

// model two-way da automação (ref do useAutomation na página). Usar
// model em vez de prop permite mutar automation.value.* aqui (form-owned
// edits) sem disparar vue/no-mutating-props.
const automation = defineModel('automation', {
  type: Object,
  required: true,
});

const { t } = useI18n();
const getters = useStoreGetters();

// Tabs: 0 = Geral (nome/descrição/evento), 1 = Condições, 2 = Ações.
const TAB_GENERAL = 0;
const TAB_CONDITIONS = 1;
const TAB_ACTIONS = 2;
const activeTab = ref(TAB_GENERAL);

const accountId = computed(() => getters.getCurrentAccountId.value);
const isFeatureEnabledonAccount = computed(
  () => getters['accounts/isFeatureEnabledonAccount'].value
);
const isFeatureEnabled = flag =>
  isFeatureEnabledonAccount.value(accountId.value, flag);

const errors = ref({});

const {
  conditionGroups,
  copyPreviousGroup,
  convertGroupsToConditions,
  initGroups,
  addGroup,
  removeGroup,
  updateGlobalGroupOperator,
  updateGroup,
  handleResetFilter,
  convertErrorKeysToGroupFormat,
} = useAutomationConditionGroups();

// Inicializa os grupos a partir das condições assim que a automação
// estiver disponível (no edit ela chega assíncrona após load-by-id).
watch(
  automation,
  newAutomation => {
    if (newAutomation) {
      initGroups(newAutomation.conditions);
    }
  },
  { immediate: true }
);

const automationRuleEvents = computed(() =>
  AUTOMATION_RULE_EVENTS.map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  }))
);

const automationActionTypes = computed(() => {
  const actionTypes = isFeatureEnabled('sla')
    ? AUTOMATION_ACTION_TYPES
    : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

  // O evento do link do webchat não aberto tem a própria lista de ações;
  // "a IA atende pelo WhatsApp" só existe nele (é ele que tem um turn
  // segurado pra liberar).
  const isUnopenedLink =
    automation.value?.event_name === WEBCHAT_LINK_UNOPENED_EVENT;
  const forEvent = actionTypes.filter(({ key }) =>
    isUnopenedLink
      ? WEBCHAT_LINK_UNOPENED_ACTIONS.includes(key)
      : key !== 'webchat_ai_on_whatsapp'
  );

  return forEvent.map(action => ({
    ...action,
    label: t(`AUTOMATION.ACTIONS.${action.label}`),
  }));
});

const hasAutomationMutated = computed(() => {
  if (conditionGroups.value.length > 0) {
    const firstGroup = conditionGroups.value[0];
    if (firstGroup.conditions[0]?.values) return true;
  }
  if (automation.value?.actions?.[0]?.action_params?.length) return true;
  return false;
});

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );

    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

// O grupo novo nasce com a condição padrão ("status"), que o evento do link
// do webchat não tem — o seletor caía no primeiro item, um SEGUNDO prazo.
const addConditionGroup = () => {
  addGroup();
  if (automation.value?.event_name !== WEBCHAT_LINK_UNOPENED_EVENT) return;

  const group = conditionGroups.value[conditionGroups.value.length - 1];
  group.conditions = group.conditions.map(condition =>
    condition.attribute_key === 'status'
      ? { ...condition, attribute_key: 'inbox_id' }
      : condition
  );
};

const handleEventChange = () => {
  props.onEventChange(automation.value);
  initGroups(automation.value.conditions);
};

const onResetFilter = (groupIndex, conditionIndex, currentCondition) => {
  handleResetFilter(
    groupIndex,
    conditionIndex,
    currentCondition,
    props.automationTypes,
    automation.value.event_name
  );
};

const submit = () => {
  // Converter grupos para condições flat antes de salvar
  automation.value.conditions = convertGroupsToConditions(
    conditionGroups.value
  );

  const validationErrors = validateAutomation(automation.value);
  // Converter chaves de erro pra formato com grupo (condition_g_c)
  errors.value = convertErrorKeysToGroupFormat(validationErrors);

  if (Object.keys(errors.value).length > 0) {
    const conditionErrors = Object.keys(errors.value).filter(k =>
      k.startsWith('condition_')
    );
    const actionErrors = Object.keys(errors.value).filter(k =>
      k.startsWith('action_')
    );

    // Leva o usuário pra aba que contém o primeiro erro (campos podem
    // estar numa aba não visível).
    if (conditionErrors.length > 0) {
      activeTab.value = TAB_CONDITIONS;
      useAlert(t('AUTOMATION.EDIT.CONDITION_VALIDATION_ERROR'));
    } else if (actionErrors.length > 0) {
      activeTab.value = TAB_ACTIONS;
      useAlert(t('AUTOMATION.EDIT.ACTION_VALIDATION_ERROR'));
    } else {
      activeTab.value = TAB_GENERAL;
      useAlert(t('AUTOMATION.EDIT.FORM_VALIDATION_ERROR'));
    }
    return;
  }

  const payload = generateAutomationPayload(automation.value);
  emit('submit', payload, props.mode);
};

const submitLabel = computed(() =>
  props.mode === 'edit'
    ? t('AUTOMATION.EDIT.SUBMIT')
    : t('AUTOMATION.ADD.SUBMIT')
);

const cancelLabel = computed(() =>
  props.mode === 'edit'
    ? t('AUTOMATION.EDIT.CANCEL_BUTTON_TEXT')
    : t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT')
);

// Form válido? Roda a mesma validação do submit de forma reativa (sem
// mutar o estado) pra manter o botão criar/salvar desabilitado enquanto
// as condições do form não forem atendidas.
const isValid = computed(() => {
  if (!automation.value) return false;
  const candidate = {
    ...automation.value,
    conditions: convertGroupsToConditions(conditionGroups.value),
  };
  return Object.keys(validateAutomation(candidate)).length === 0;
});

// Indica se uma aba tem erro de validação (mostra marcador vermelho).
const tabHasError = tabId => {
  const keys = Object.keys(errors.value);
  if (tabId === TAB_CONDITIONS) {
    return keys.some(k => k.startsWith('condition_'));
  }
  if (tabId === TAB_ACTIONS) {
    return keys.some(k => k.startsWith('action_'));
  }
  return Boolean(
    errors.value.name || errors.value.description || errors.value.event_name
  );
};

const tabs = computed(() => [
  {
    id: TAB_GENERAL,
    label: t('AUTOMATION.TABS.GENERAL'),
    icon: 'i-lucide-info',
  },
  {
    id: TAB_CONDITIONS,
    label: t('AUTOMATION.TABS.CONDITIONS'),
    icon: 'i-lucide-filter',
  },
  {
    id: TAB_ACTIONS,
    label: t('AUTOMATION.TABS.ACTIONS'),
    icon: 'i-lucide-zap',
  },
]);
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Área scrollável: altura fixa (flex-1), rola internamente. Trocar de
         aba não muda a altura do form nem move o footer. -->
    <div class="flex-1 min-h-0 overflow-auto">
      <div class="w-[48rem] max-w-full px-6 py-6">
        <!-- Tab bar custom: underline accent + ícone + marcador de erro -->
        <div class="flex items-center gap-1 border-b border-n-weak">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="relative flex items-center gap-2 px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors duration-150 outline-none"
            :class="
              activeTab === tab.id
                ? 'text-n-blue-11 border-n-blue-9'
                : 'text-n-slate-11 border-transparent hover:text-n-slate-12 hover:border-n-slate-6'
            "
            @click="activeTab = tab.id"
          >
            <span class="size-4" :class="tab.icon" />
            {{ tab.label }}
            <span
              v-if="tabHasError(tab.id)"
              class="size-1.5 rounded-full bg-n-ruby-9"
            />
          </button>
        </div>

        <!-- Aba Geral: Nome, Descrição e Evento -->
        <div v-show="activeTab === TAB_GENERAL" class="mt-6">
          <div class="grid grid-cols-2 gap-4 mb-4">
            <woot-input
              v-model="automation.name"
              :label="t('AUTOMATION.ADD.FORM.NAME.LABEL')"
              type="text"
              :class="{ error: errors.name }"
              :error="errors.name ? t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
              :placeholder="t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
            />
            <woot-input
              v-model="automation.description"
              :label="t('AUTOMATION.ADD.FORM.DESC.LABEL')"
              type="text"
              :class="{ error: errors.description }"
              :error="
                errors.description ? t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
              "
              :placeholder="t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
            />
          </div>

          <div>
            <label :class="{ error: errors.event_name }">
              {{ t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
              <select
                v-model="automation.event_name"
                class="m-0"
                @change="handleEventChange"
              >
                <option
                  v-for="event in automationRuleEvents"
                  :key="event.key"
                  :value="event.key"
                >
                  {{ event.value }}
                </option>
              </select>
              <span v-if="errors.event_name" class="message">
                {{ t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
              </span>
            </label>
            <p
              v-if="hasAutomationMutated"
              class="text-xs text-right text-n-teal-10 pt-1"
            >
              {{ t('AUTOMATION.FORM.RESET_MESSAGE') }}
            </p>
          </div>
        </div>

        <!-- Aba Condições -->
        <section v-show="activeTab === TAB_CONDITIONS" class="mt-6">
          <label>
            {{ t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
          </label>
          <div class="w-full mb-4">
            <div class="grid gap-4">
              <AutomationConditionGroup
                v-for="(group, groupIndex) in conditionGroups"
                :key="group.id"
                :model-value="group"
                :group-index="groupIndex"
                :is-only-group="conditionGroups.length === 1"
                :is-first-operator="groupIndex === 1"
                :filter-attributes="
                  getTranslatedAttributes(
                    automationTypes,
                    automation.event_name
                  )
                "
                :all-custom-attributes="allCustomAttributes"
                :automation-types="automationTypes"
                :automation="automation"
                :mode="mode"
                :errors="errors"
                :get-input-type="getInputType"
                :get-operators="getOperators"
                :get-condition-dropdown-values="getConditionDropdownValues"
                :get-custom-attribute-type="getCustomAttributeType"
                @update:model-value="updateGroup(groupIndex, $event)"
                @remove="removeGroup"
                @update:group-operator="updateGlobalGroupOperator"
                @reset-filter="onResetFilter"
              />
            </div>

            <div
              class="flex gap-2 justify-between pt-4 mt-4 border-t border-n-weak"
            >
              <div class="flex items-center gap-3">
                <NextButton
                  sm
                  ghost
                  blue
                  icon="i-lucide-layers"
                  :label="t('AUTOMATION.CONDITION.ADD_CONDITION_GROUP')"
                  @click="addConditionGroup"
                />
                <label
                  class="flex items-center gap-2 text-xs text-n-slate-11 cursor-pointer"
                >
                  <Switch v-model="copyPreviousGroup" size="small" />
                  {{ t('AUTOMATION.CONDITION.COPY_PREVIOUS_GROUP') }}
                </label>
              </div>
            </div>
          </div>
        </section>

        <!-- Aba Ações -->
        <section v-show="activeTab === TAB_ACTIONS" class="mt-6">
          <label>
            {{ t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
          </label>
          <p
            v-if="automation.event_name === WEBCHAT_LINK_UNOPENED_EVENT"
            class="mb-3 text-sm text-n-slate-11"
          >
            {{ t('AUTOMATION.ADD.FORM.ACTIONS.WEBCHAT_LINK_UNOPENED_HINT') }}
          </p>
          <div
            class="w-full p-4 mb-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
          >
            <AutomationActionInput
              v-for="(action, i) in automation.actions"
              :key="i"
              v-model="automation.actions[i]"
              :action-types="automationActionTypes"
              :dropdown-values="getActionDropdownValues(action.action_name)"
              :show-action-input="
                showActionInput(automationActionTypes, action.action_name)
              "
              :error-message="
                errors[`action_${i}`]
                  ? t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                  : ''
              "
              :initial-file-name="getFileName(action, automation.files)"
              @reset-action="resetAction(i)"
              @remove-action="removeAction(i)"
            />
            <div class="mt-4">
              <NextButton
                icon="i-lucide-plus"
                blue
                faded
                sm
                :label="t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
                @click="appendNewAction"
              />
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Footer fixo no rodapé do form: shrink-0, sempre embaixo, não se move
         ao trocar de aba (a área das abas tem altura fixa e rola interna). -->
    <div class="shrink-0 border-t bg-n-background border-n-weak">
      <div
        class="flex flex-row justify-end w-[48rem] max-w-full gap-2 px-6 py-4"
      >
        <NextButton
          faded
          slate
          type="reset"
          :label="cancelLabel"
          :disabled="isSubmitting"
          @click.prevent="emit('cancel')"
        />
        <NextButton
          solid
          blue
          type="submit"
          :label="submitLabel"
          :is-loading="isSubmitting"
          :disabled="isSubmitting || !isValid"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
